import React from 'react';
import { DocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import List, { ListProps } from './List';
import { LoadingParams } from './ListContext';

interface GraphQLListProps extends Omit<ListProps, 'getDataMethod'> {
  query: DocumentNode;
  variables?: Record<string, any>;
  dataPath: string;
  transformVariables?: (params: LoadingParams) => Record<string, any>;
  transformData?: (data: any) => Promise<{ result: any[]; totalPages?: number; totalElements?: number }>;
}

const GraphQLList: React.FC<GraphQLListProps> = ({
  query,
  variables = {},
  dataPath,
  transformVariables,
  transformData,
  children,
  ...listProps
}) => {
  const { refetch } = useQuery(query, {
    variables,
    skip: true,
  });

  const getDataMethod = async (loadParams: LoadingParams) => {
    try {
      const queryVariables = transformVariables ? transformVariables(loadParams) : variables;

      const result = await refetch(queryVariables);
      const rawData = result.data;

      if (!rawData) {
        return { result: [], totalPages: 1, totalElements: 0 };
      }

      const listData = dataPath.split('.').reduce((obj: any, key: string) => obj?.[key], rawData);

      if (transformData) {
        return await transformData(listData);
      }

      if (Array.isArray(listData)) {
        return {
          result: listData,
          totalPages: 1,
          totalElements: listData.length,
        };
      }

      return {
        result: listData?.items || listData?.result || [],
        totalPages: listData?.totalPages || 1,
        totalElements: listData?.totalElements || listData?.total || 0,
      };
    } catch (err) {
      return { result: [], totalPages: 1, totalElements: 0 };
    }
  };

  return (
    <List getDataMethod={getDataMethod} {...listProps}>
      {children}
    </List>
  );
};

export default GraphQLList;
